export interface ParsedPart {
  text: string;
  isThought: boolean;
  isToolStatus?: boolean;
}

function isThoughtPart(part: Record<string, unknown>): boolean {
  if (!part || typeof part !== 'object') return false;

  if (part.thought === true || part.thought === 'true') return true;
  if ('thought' in part && part.thought !== false) return true;
  if (part.role === 'thought' || part.type === 'thought') return true;

  return false;
}

export function extractTextParts(payload: Record<string, unknown>): ParsedPart[] {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  // OLD FORMAT: Direct content.parts with text
  const content = payload.content as Record<string, unknown> | undefined;
  const message = payload.message as Record<string, unknown> | undefined;
  const candidates = payload.candidates as Record<string, unknown>[] | undefined;

  const candidateContent = content
    || message?.content
    || (candidates?.[0] as Record<string, unknown>)?.content
    || ((candidates?.[0] as Record<string, unknown>)?.message as Record<string, unknown>)?.content;

  const parts = (candidateContent as Record<string, unknown>)?.parts
    || (payload.parts as Record<string, unknown>[])
    || (message?.parts as Record<string, unknown>[])
    || (Array.isArray(payload.content) ? (payload.content as Record<string, unknown>[]) : null);

  if (Array.isArray(parts)) {
    return parts
      .filter((part) => part && typeof part === 'object')
      .flatMap((part: Record<string, unknown>) => {
        // Check for direct text (preserve whitespace/newline tokens required for markdown)
        if (typeof part.text === 'string' && part.text.length > 0) {
          return [{
            text: part.text,
            isThought: isThoughtPart(part),
          }];
        }

        // NEW FORMAT: Extract tool name from function calls/responses for thoughts
        if (part.functionCall && typeof part.functionCall === 'object') {
          const toolName = (part.functionCall as Record<string, unknown>)?.name;
          if (typeof toolName === 'string') {
            return [{
              text: `🔍 Using ${toolName}...`,
              isThought: true,
              isToolStatus: true,
            }];
          }
        }

        if (part.functionResponse && typeof part.functionResponse === 'object') {
          const toolName = (part.functionResponse as Record<string, unknown>)?.name;
          if (typeof toolName === 'string') {
            return [{
              text: `✓ ${toolName} completed`,
              isThought: true,
              isToolStatus: true,
            }];
          }
        }

        return [];
      });
  }

  // OLD FORMAT: Direct text field
  if (typeof payload.text === 'string' && payload.text.length > 0) {
    return [{ text: payload.text, isThought: false }];
  }

  // OLD FORMAT: Nested content.text
  const contentText = (content as Record<string, unknown>)?.text;
  if (typeof contentText === 'string' && contentText.length > 0) {
    return [{ text: contentText, isThought: false }];
  }

  // Handle nested response/data structures (backwards compat)
  if (payload.response && typeof payload.response === 'object') {
    return extractTextParts(payload.response as Record<string, unknown>);
  }

  if (payload.data && typeof payload.data === 'object') {
    return extractTextParts(payload.data as Record<string, unknown>);
  }

  // OLD FORMAT: Candidates array
  if (Array.isArray(payload.candidates)) {
    return (payload.candidates as Record<string, unknown>[])
      .map((candidate) => (typeof (candidate as Record<string, unknown>)?.text === 'string' ? (candidate as Record<string, unknown>).text : ''))
      .filter(Boolean)
      .map((text) => ({ text: text as string, isThought: false }));
  }

  return [];
}
